/* eslint-disable no-redeclare */
import { FC, useEffect, useMemo } from 'react';

import { useAtomValue } from 'jotai';
import { useNavigate } from 'react-router';

import { addTag } from '@core';
import { useToggle } from '@hooks';
import { useLayouts, useUserClubs } from '@queries';
import { activeAnalysisAtom, activeSwingIdAtom, activeUserIdAtom, activityAtom } from '@store';
import { ActionButton, colors, PropertyControl } from '@ui';

import { ComparisonSwingModal } from '../modals/ComparisonSwingModal/ComparisonSwingModal';
import { useComparisonSwing } from '../modals/ComparisonSwingModal/useComparisonSwing';
import { LayoutPresetModal } from '../modals/LayoutPresetModal';

import { useNavigationActivityTags } from './useNavigationActivityTags';

type WithLink = { url: () => string };
type WithDynamicLink = { url: (arg: any) => string };

const MAX_CHARS = 8;

function trim(text: string): string;
function trim(text?: string | null): string | null | undefined;
function trim(text?: string | null) {
    return Number(text?.length) > MAX_CHARS ? text!.slice(0, 9) + '...' : text;
}

export const Club: FC<WithLink> = ({ url }) => {
    const navigate = useNavigate();
    const activity = useAtomValue(activityAtom);
    const { data: userClubs } = useUserClubs();
    const currentClub = activity?.club ? { label: activity.club.shortName } : { label: 'Club' };
    return (
        <PropertyControl
            values={[currentClub]}
            onClick={() => navigate(`${url()}?display=${!userClubs?.length ? 'all' : 'bag'}`)}
        />
    );
};

export const Tags: FC<WithLink> = ({ url }) => {
    const navigate = useNavigate();
    const { currentTags } = useNavigationActivityTags();
    return (
        <PropertyControl
            values={currentTags.length ? currentTags : [{ label: 'Tag', icon: 'tag' }]}
            onClick={() => navigate(url())}
        />
    );
};

export const Layout: FC<WithDynamicLink> = ({ url }) => {
    const navigate = useNavigate();
    const activeUserId = useAtomValue(activeUserIdAtom);
    const activity = useAtomValue(activityAtom);
    const activeLayoutId = activity?.layoutId;
    const { data: layoutList, isLoading: areLayoutsLoading } = useLayouts();

    const layout = useMemo(
        () => layoutList?.find((layout) => layout.id === activeLayoutId),
        [layoutList, activeLayoutId],
    );

    useEffect(() => {
        addTag('layoutId', activeLayoutId || null);
    }, [activeLayoutId]);

    const [isLayoutPresetModalOpen, toggleLayoutPresetModal] = useToggle();

    return (
        <>
            <PropertyControl
                values={[
                    { label: areLayoutsLoading ? 'Loading...' : trim(layout?.name) ?? 'No Layout', icon: 'layout' },
                ]}
                onClick={toggleLayoutPresetModal}
            />
            {isLayoutPresetModalOpen && (
                <LayoutPresetModal
                    onDismiss={toggleLayoutPresetModal}
                    onEdit={(layoutId: number) => navigate(url(layoutId))}
                    activeUserId={activeUserId}
                />
            )}
        </>
    );
};

export const Comparison = () => {
    const { toggleModal, openModal, comparisonLabel } = useComparisonSwing();
    return (
        <>
            <PropertyControl
                values={[
                    {
                        label: trim(comparisonLabel),
                        icon: 'comparison',
                    },
                ]}
                onClick={toggleModal}
            />
            {openModal && <ComparisonSwingModal open onDismiss={toggleModal} />}
        </>
    );
};

export const Visualizer3d: FC<WithLink> = ({ url }) => {
    const navigate = useNavigate();
    const activeSwingId = useAtomValue(activeSwingIdAtom);
    const activeAnalyisis = useAtomValue(activeAnalysisAtom);
    const hasFullAnalysis = !!activeAnalyisis?.fullAnalysis;
    return (
        <ActionButton
            isDisabled={!hasFullAnalysis || !activeSwingId}
            icon="threed"
            iconSize="large"
            variant="tertiary"
            iconColor={colors.blue[500]}
            iconWeight="fill"
            onClick={() => navigate(url())}
        />
    );
};

export const Playback: FC<WithLink> = ({ url }) => {
    const navigate = useNavigate();
    const activeSwingId = useAtomValue(activeSwingIdAtom);
    const activeAnalyisis = useAtomValue(activeAnalysisAtom);
    const hasAnalysis = !!(activeAnalyisis?.fullAnalysis ?? activeAnalyisis?.quickAnalysis);

    return (
        <ActionButton
            isDisabled={!hasAnalysis || !activeSwingId}
            icon="camera"
            iconSize="large"
            variant="tertiary"
            iconColor={colors.blue[500]}
            iconWeight="fill"
            onClick={() => navigate(url())}
        />
    );
};
